import { defineChain } from 'viem';

import { getContrastColor } from '@infinex/ui-utils/color';

import { envOverrides } from '../../env';
import {
  DEFAULT_CHAIN_LIMITS,
  defaultServices,
  defaultSwidge,
} from '../configHelpers';
import type { AllChainConfig } from '../configTypes';

const viemHyperEVM = /*#__PURE__*/ defineChain({
  id: 999,
  name: 'HyperEVM',
  nativeCurrency: {
    decimals: 18,
    name: 'HYPE',
    symbol: 'HYPE',
  },
  contracts: {
    multicall3: {
      address: '0xcA11bde05977b3631167028862bE2a173976CA11',
      blockCreated: 13051,
    },
  },
  rpcUrls: {
    default: { http: ['https://rpc.hyperliquid.xyz/evm'] },
  },
  blockExplorers: {
    default: {
      name: 'HyperEVM Explorer',
      url: 'https://hyperliquid.cloud.blockscout.com/',
    },
  },
  testnet: false,
});

export const hyperEVM = {
  displayName: 'HyperEVM',
  runtime: 'evm',
  viemChain: viemHyperEVM,
  isSupported: true,
  isReleased: true,
  isMainnet: true,
  orderPriority: 10,

  decoration: {
    color: '#97FCE4',
    contrastColor: getContrastColor('#97FCE4'),
  },
  features: {
    wallet: { supported: true, import: false },
    vault: {
      supported: false,
      ccqRecovery: false,
    },
  },
  connectivity: {
    rpc: {
      quicknode: {
        chainKey: 'hype-mainnet',
        urlSuffix: 'evm',
      },
    },
    blockExplorers: {
      primary: {
        base: 'https://hyperevmscan.io',
        token: 'https://hyperevmscan.io/token',
      },
    },
  },
  environments: {
    prod: true,
    staging: true,
    test: true,
    dev: true,
  },
  swidge: defaultSwidge({
    debridge: {
      chainKey: '100000022',
      providerContract: '0x43dE2d77BF8027e25dBD179B491e8d64f38398aA',
    },
    stargate: {
      chainKey: 'hyperliquid',
    },
    kyberswap: { chainKey: 'hyperevm' },
  }),
  services: defaultServices({
    codex: {
      networkId: 999,
      nativeLiquidityAddress: '0x2222222222222222222222222222222222222222',
    },
    defiLlama: { chainKeys: ['hyperevm', 'hyperliquid'] },
    trm: { chainKey: 'hyperliquid' },
    coingecko: {
      networkId: 'hyperevm',
      platformId: 'hyperevm',
    },
    coingeckoDex: {
      networkId: 'hyperevm',
    },
    zerion: {
      chainKey: 'hyperevm',
    },
    qnStreams: {
      networkKey: 'hyperevm-mainnet',
    },
    // STS URL path is `hyper` (not Config API shortName `hyper-evm`).
    safe: { shortName: 'hyper' },
    alchemy: { chainSlug: 'hyperliquid-mainnet' },
    pimlico: { chainSlug: 'hyper-evm' },
  }),
  limits: envOverrides(DEFAULT_CHAIN_LIMITS, {}),
} as const satisfies AllChainConfig<'evm', true>;
